/**
 * WEB-785: служебные строки паблик-рендера квиза (счётчик шагов, лоадер,
 * подписи графика/«до-после», соц-доказательство рейтинга) были жёстко
 * зашиты по-английски во фронте и всплывали поверх русского (и любого
 * другого) контента воронки, когда AI-генерация не заполнила
 * соответствующее опциональное поле блока (title/steps/startLabel/...).
 *
 * Локализация по языку воронки (funnel.language / quiz.language, BCP-47,
 * напр. "ru", "ru-RU", "en"). Неизвестный/отсутствующий язык → фолбэк 'en'
 * (прежнее поведение не меняется для уже опубликованных en-воронок и для
 * мест, где локаль ещё не прокинута).
 *
 * Структура расширяемая: добавить язык = один блок в DICTIONARIES.
 */


import type { HandoffPackStrings } from './handoffSuccessStrings';
import {
  registerLegalDocPatternsSource,
  type LegalDocPatternSources,
} from './legalDocNames.public';

export interface LoaderStepText {
  label: string;
  durationMs: number;
}

/**
 * WEB-1905: надписи страницы управления подпиской (портал покупателя:
 * список подписок, отмена с подтверждением, возобновление, оффер
 * удержания, вход по ссылке из письма). Раньше это были английские
 * константы `PORTAL_TEXTS` внутри самого портала, а дата окончания
 * периода была прибита к `en` (фикс F1 ревизии пачки 15). Портал — часть
 * ПУБЛИЧНОЙ страницы воронки, поэтому его язык — язык воронки, как у
 * плеера, а не язык браузера и не английский всегда.
 */
export interface PublicPortalStrings {
  invalidLink: string;
  expiredCode: string;
  expiredManageLink: string;
  sessionExpired: string;
  loading: string;
  loadError: string;
  empty: string;
  cancelError: string;
  cancelConfirmTitle: string;
  cancelConfirmKeep: string;
  cancelConfirmProceed: string;
  resumeError: string;
  resumePeriodEnded: string;
  resumeSuccess: string;
  downsellError: string;
  downsellNotEligible: string;
  downsellUnavailable: string;
  downsellSuccess: string;
  entryPrompt: string;
  entrySubmit: string;
  entrySent: string;
  entrySendError: string;
  /**
   * WEB-3413: форма по номеру ссылки «Управлять подпиской» из письма после
   * оплаты (`?entry=`) — пояснение, кнопка и «отправлено» с МАСКОЙ почты
   * покупки (`s***@gmail.com`), переход к обычной форме с другой почтой.
   */
  entryNumberPrompt: string;
  entryNumberSubmit: (maskedEmail: string) => string;
  entryNumberSent: (maskedEmail: string) => string;
  entryNumberOtherEmail: string;
  /**
   * Фолбэк заголовка списка подписок, когда автор его не задал. Ревизия
   * пачки 16 (находка Ф2): раньше конструктор писал сюда английское «Your
   * subscriptions» прямо в содержимое блока — покупатель русской воронки
   * читал английский заголовок, а автор ещё и платил кредитами за перевод
   * нашего же умолчания.
   */
  listTitle: string;
  /** Фолбэк надписи кнопки отмены, когда автор её не задал. */
  cancelLabel: string;
  /** Фолбэк надписи кнопки возобновления. */
  resumeLabel: string;
  /** Фолбэки заголовка и кнопки карточки оффера удержания. */
  offerTitle: string;
  offerCta: string;
  cancellationScheduled: string;
  subscriptionCanceled: string;
  /**
   * Пример адреса в поле почты. НЕ переводится: это образец адреса, а не
   * фраза, — один на все языки (PORTAL_EMAIL_PLACEHOLDER).
   */
  entryEmailPlaceholder: string;
  newPrice: (formatted: string) => string;
  cancellationScheduledOn: (date: string) => string;
  renewsOn: (date: string) => string;
  /** Статус подписки Stripe → слово покупателю; ключ = статус Stripe. */
  statusLabels: Readonly<Record<string, string>>;
}

/**
 * WEB-2115 — надписи страницы ПОЛУЧАТЕЛЯ ссылки предпросмотра (заслонка кода,
 * отказы, переключатель версий). Читатель — посторонний человек, которому
 * автор прислал ссылку, поэтому язык — язык его браузера или воронки.
 *
 * Английский и русский живут НЕ в ядре этого файла, а в
 * `utils/previewLinkStrings.ts`, который грузится только ленивой страницей
 * предпросмотра: у первого экрана покупателя запаса веса почти нет, а этих
 * надписей обычной воронке не нужно ни байта. Остальные языки едут в своих
 * языковых пакетах (они и так ленивые) полем `previewLink`.
 */
export interface PreviewLinkStrings {
  gateTitle: string;
  gateHint: string;
  gatePlaceholder: string;
  gateOpen: string;
  /** Отказ внутри заслонки: сам показ заслонки уже говорит, что ссылка жива. */
  wrong: string;
  /** Счётчика оставшихся попыток наружу нет — только факт «кончились». */
  lockedTitle: string;
  lockedSub: string;
  /** Один экран на протухшую, отозванную и несуществующую ссылку. */
  deadTitle: string;
  deadSub: string;
  banner: string;
  /** Шаблон с `{n}` и `{total}`: «Версия 3 из 7». */
  versionOf: string;
  versionPicker: string;
  versionPrev: string;
  versionNext: string;
  /**
   * WEB-2980: подпись под «временно недоступно», когда дверь ответила 429 со
   * сроком (`details.minutes`, WEB-2932). Шаблон с `{minutes}`; единица —
   * сокращение «мин.», чтобы не склонять число на тридцати языках.
   */
  retryIn: string;
}

/**
 * WEB-1903: УМОЛЧАНИЯ СОДЕРЖИМОГО НОВЫХ БЛОКОВ КОНСТРУКТОРА.
 *
 * Это не надписи плеера, а ЗНАЧЕНИЯ, которые конструктор кладёт в новый блок
 * и которые дальше живут как авторский текст воронки. Язык у них тот же —
 * язык ВОРОНКИ: одиннадцать типов блоков писали сюда русский литерал, и автор
 * английской воронки получал русский текст в содержимое (а потом платил
 * кредитами за перевод собственного дефолта).
 *
 * Ключи держит `buildNewBlockDefaults` (`store/editor/blockDefaults.ts`,
 * WEB-2058) и кнопка «Добавить
 * тариф» в панели свойств; сторож «в buildNewBlockDefaults нет кириллицы»
 * (`store/__tests__/editorSlice.blockDefaultsNoCyrillic.web1903.test.ts`) не
 * даёт классу вернуться на новом типе блока.
 */
export interface PublicQuizBlockDefaults {
  /** micro_feedback: сочувственная реплика и пояснение под ней. */
  microFeedbackTitle: string;
  microFeedbackDetail: string;
  /** rating_chip: аудитория рядом с оценкой («12 000 отзывов»). */
  ratingAudience: string;
  /** Вопрос «насколько это про вас» — общий у likert_scale и statement. */
  selfRelevanceQuestion: string;
  likertDisagreeLabel: string;
  likertAgreeLabel: string;
  /** scratch_card: надпись на слое, заголовок и приписка под ним. */
  scratchCoverLabel: string;
  scratchRevealTitle: string;
  scratchRevealNote: string;
  /** blur_teaser: заголовок, две строки-заготовки и приписка. */
  blurTeaserTitle: string;
  blurTeaserFirstLabel: string;
  blurTeaserFirstTeaser: string;
  blurTeaserSecondLabel: string;
  blurTeaserSecondTeaser: string;
  blurTeaserNote: string;
  forecastTitle: string;
  promoTicketTitle: string;
  nowGoalTitle: string;
  /** «Показатель 1» / «Metric 1» — подписи полос блока «Что изменится». */
  metricLabel: (index: number) => string;
  /** bmi_scale: заголовок, подпись значения, четыре зоны и дисклеймер. */
  bmiTitle: string;
  bmiValueLabel: string;
  bmiZoneUnderweight: string;
  bmiZoneNormal: string;
  bmiZoneOverweight: string;
  bmiZoneObese: string;
  bmiNote: string;
  /** statement_agreement: утверждение и три реакции. */
  statementText: string;
  statementDisagreeLabel: string;
  statementNeutralLabel: string;
  statementAgreeLabel: string;
  /** preference_card: вопрос и две реакции. */
  preferenceQuestion: string;
  preferenceNoLabel: string;
  preferenceYesLabel: string;
  /** plan_selector: бейдж тарифа, добавленного кнопкой в панели свойств. */
  planBadge: string;
  /** loader_processing: заголовок экрана обработки (был английским всегда). */
  loaderProcessingTitle: string;
  /**
   * WEB-1907 — ВТОРАЯ ПОЛОВИНА ТОГО ЖЕ КЛАССА. WEB-1903 увела отсюда русские
   * литералы, а английские остались: русская воронка получала в содержимое
   * «Important information», «Only 3 left!», «John D. — Great product!».
   * Ключи ниже — ровно те строки; нумераторы («Option 1», «FAQ 1», «Select...»)
   * сюда НЕ едут: их не переводим по решению ревизии пачки 3.
   */
  /** icon_with_text: подпись рядом со значком. */
  iconWithTextTitle: string;
  /** accordion: тело первой заготовки (заголовок — нумератор «FAQ 1»). */
  accordionAnswer: string;
  /** card: заголовок и описание карточки. */
  cardTitle: string;
  cardDescription: string;
  /** Плейсхолдеры полей: дата, число, имя. */
  datePickerPlaceholder: string;
  numberInputPlaceholder: string;
  nameFieldPlaceholder: string;
  /** rating_stars: просьба оценить. */
  ratingStarsLabel: string;
  /** toggle: подпись переключателя. */
  toggleLabel: string;
  /**
   * WEB-2158 + WEB-2162 п.3: тексты ДВУХ независимых галочек блока явного
   * согласия — на СБОР ответов об особых категориях и на ПЕРЕДАЧУ их третьим
   * лицам. Утверждение от первого лица («Я даю согласие…»), а не «Мне понятно,
   * что…»: форма осведомлённости согласием не является.
   */
  sensitiveConsentCollect: string;
  sensitiveConsentShare: string;
  /** testimonials: имя автора отзыва-заготовки и сам отзыв. */
  testimonialName: string;
  testimonialText: string;
  /** feature_chips: четыре чипа-заготовки, по порядку. */
  featureChipLabels: readonly [string, string, string, string];
  /** guarantee_badge / stock_badge / urgency_banner: тексты доверия и срочности. */
  guaranteeBadge: string;
  stockBadge: string;
  urgencyBanner: string;
  /** callout: врезка «важное». */
  calloutText: string;
  /** multi_step_question: заголовок серии вопросов. */
  multiStepQuestionTitle: string;
  /** photo_upload: просьба приложить фото. */
  photoUploadQuestion: string;
  /**
   * payment_terms: шаблон юридической строки под кнопкой оплаты.
   *
   * ПЛЕЙСХОЛДЕР `{{price}}` ОБЯЗАТЕЛЕН В КАЖДОМ ЯЗЫКЕ: его подставляет
   * `interpolatePaymentTerms` (PublicBlockRendererExtended) ценой, которую
   * реально спишет кнопка. Перевод без плейсхолдера — юр-текст без суммы;
   * равенство держит сторож полноты `publicQuizStrings.blockDefaults.web1903`.
   */
  paymentTermsTemplate: string;
  /** downsell_popup: заголовок и кнопка попапа с удержанием. */
  downsellTitle: string;
  downsellCta: string;
  /**
   * select_dropdown: подсказка в пустом пункте списка. Ревизия WEB-1907:
   * односложная, поэтому мимо правила «латинская ФРАЗА» она проезжала — а
   * покупатель русской воронки читал английское «Select...» в самом видном
   * месте поля. Сами варианты списка остаются нумераторами («Option 1»).
   */
  selectPlaceholder: string;
}

/**
 * WEB-2975: экран после оплаты пейволла (см. поле `purchaseThanks` ниже и
 * `purchaseThanksStrings.ts`). `body` — шаблон с одной подстановкой `{name}`:
 * название пейволла печатается жирным, поэтому шаблон делится вокруг него.
 */
export interface PurchaseThanksStrings {
  readonly heading: string;
  readonly body: string;
  /** Покупатель пришёл из приложения: вернуться туда, доступ включается. */
  readonly appReturn: string;
  readonly closeTab: string;
}

/**
 * WEB-2977: надписи страниц ПОСЛЕ ОПЛАТЫ — кнопки «Закрыть» и «Открываем
 * приложение…» экрана пейволла и все подписи трёх страниц `/handoff/*` — у
 * языков пакета. Ядро ru/en — в `handoffSuccessStrings.ts` (ленивые страницы,
 * первый экран его не везёт); читать — только через
 * `getHandoffSuccessStrings`.
 *
 * ПОЧЕМУ ВЛОЖЕНО В `purchaseThanks`, а не своим полем пакета: своё поле стоит
 * строку передачи в `packToStrings`, а это код ПЕРВОГО экрана (+22 байта
 * сжатых у `purchaseThanks`, WEB-2975), запаса у потолка веса нет.
 * `purchaseThanks` уже передаётся целиком — вложенное поле едет бесплатно.
 */
export interface PurchaseThanksPackStrings extends PurchaseThanksStrings {
  readonly handoff: HandoffPackStrings;
}

/**
 * WEB-3826 (сводный юнит, часть «системные»): системная страница воронки —
 * заголовок и строка под ним (эталон `System`,
 * `design/cabinet-rescin/sections/auth-out.jsx`, тексты `TXO.*.sys`) — по паре
 * на состояние, плюс «Войти» страницы «только автору». Прежние одиночные фразы
 * (`pageNotFound`, `funnelUnavailable`, …) сняты — пользователей не
 * осталось. `serviceUnavailable` читателей больше нет: страница предпросмотра с WEB-4118 берёт
 * заголовок сбоя `systemPage.service` (текст эталона); снять ключ из ядра и пакетов — в отложенное. Читает — `components/public/PublicSystemPage.tsx`.
 */
export interface PublicSystemPageStrings {
  readonly page: readonly [title: string, sub: string];
  readonly funnel: readonly [title: string, sub: string];
  readonly quiz: readonly [title: string, sub: string];
  readonly paywall: readonly [title: string, sub: string];
  readonly service: readonly [title: string, sub: string];
  readonly author: readonly [title: string, sub: string];
  readonly signIn: string;
}

export interface PublicQuizStrings {
  /** Счётчик шага визарда / фолбэк блока progress_text без stepWord. */
  stepOf: (current: number, total: number) => string;
  /**
   * Слово разделителя счётчика шапки «9 of 15» / «9 из 15» (поле
   * `settings.quizHeader.counterSeparator = 'word'`, stylepack/header-counter-look).
   */
  counterOf: string;
  loaderTitle: string;
  loaderSteps: LoaderStepText[];
  reviewsBasedOn: (count: number) => string;
  chartStartLabel: string;
  chartEndLabel: string;
  beforeLabel: string;
  afterLabel: string;
  /** WEB-785 (ретест QA 27.07): навигация — Back/Continue торчали английскими. */
  backLabel: string;
  continueLabel: string;
  finishLabel: string;
  /**
   * WEB-926: обход стирания скретч-карты — таб-стоп для клавиатуры и для всех,
   * кто не может (или не понял, что надо) тереть слой пальцем.
   */
  scratchRevealLabel: string;
  /** WEB-926: статус размытой карточки blur-тизера для скринридера. */
  blurTeaserLockedLabel: string;
  /** WEB-926: название графика-прогноза для скринридера (SVG озвучивать нечем). */
  forecastChartLabel: string;
  /** WEB-926: что именно истекает у таймера в промо-тикете. */
  promoTicketTimerLabel: string;
  /** WEB-926: подписи колонок сравнения, когда автор их стёр. */
  nowGoalNowLabel: string;
  nowGoalGoalLabel: string;
  /** WEB-926: название шкалы ИМТ для скринридера (полосу озвучивать нечем). */
  bmiScaleLabel: string;
  /** WEB-967: подпись обязательного незаполненного поля (был русский хардкод). */
  requiredFieldWarning: string;
  /**
   * WEB-1716: подпись поля почты, в которое ввели не похожее на адрес
   * (`ivan@mail`). Раньше такой покупатель уходил дальше без единой ошибки, а
   * лид по нему не создавался вовсе.
   */
  invalidEmailWarning: string;
  /** WEB-967: aria-подпись кнопки закрытия пейволла. */
  closeLabel: string;
  /**
   * WEB-1428 (живой прогон PM 17.08): русские хардкоды публичного рендера —
   * кнопка «Пропустить» модалки лоадера торчала русской на en-квизе (и ИИ-
   * редактор её не достаёт: это системная строка, не контент). Заодно
   * переведены на словарь остальные видимые покупателю литералы.
   */
  loaderSkipLabel: string;
  unmuteVideoAria: string;
  playVideoAria: string;
  ratingAria: (value: number, points: number) => string;
  planNoPriceFallback: string;
  returnToAppFallback: string;
  openingAppHint: string;
  imageLoadError: string;
  /** WEB-1336 (П2): временная недоступность — НЕ «не найдено». */
  serviceUnavailable: string;
  retry: string;
  /** WEB-967: чип рейтинга «4.8 из 5» (было русское «из» на всех языках). */
  ratingOutOf: (value: number, scale: number) => string;
  /** WEB-967: тексты отказа оплаты (checkoutErrorText, WEB-933 п.2). */
  checkoutUnavailable: string;
  checkoutPreviewOnly: string;
  checkoutNoPrice: string;
  checkoutAcceptTerms: string;
  checkoutFillRequired: string;
  checkoutStartFailed: string;
  /**
   * Свежий взгляд пачки 31, п.1: чанк клиента чекаута пропал с CDN (выкат
   * фронта посреди сессии покупателя), и даже однократная перезагрузка за
   * свежим index.html не помогла. Отдельный текст нужен потому, что общий
   * «не удалось начать оплату» зовёт нажать ещё раз, а повтор в этом
   * положении не сработает никогда — лечит только перезагрузка страницы.
   */
  checkoutReloadNeeded: string;
  /**
   * WEB-1820 (возврат Д21 13.09): отказ сервера на кассе показывается на
   * языке ВОРОНКИ, по машинному коду ответа. Два текста заведены отдельно,
   * потому что совет у них разный: цены больше нет — обновить страницу или
   * написать продавцу; отказ по идентификатору покупателя — в поддержку
   * (повтор нажатия не поможет ни там, ни там).
   */
  checkoutPriceUnavailable: string;
  /**
   * WEB-2975: надписи экрана после оплаты пейволла — ТОЛЬКО у языков пакета.
   * Ядро (en, ru) едет каждому покупателю в первом экране, а этот экран рисует
   * ленивая страница пейволла, поэтому ru/en держит `purchaseThanksStrings.ts`
   * (его импортирует только страница пейволла), а читать надписи — только
   * через `getPurchaseThanksStrings`: там же выбор «пакет или ядро модуля».
   */
  purchaseThanks?: PurchaseThanksPackStrings;
  /**
   * WEB-3826: заголовок и строка системных страниц (см. `PublicSystemPageStrings`).
   * Заменили одиночные фразы `pageNotFound`, `funnelUnavailable`, `quizNotFound`,
   * `paywallNotFound`, `previewRequiresLogin` (WEB-2024, WEB-2975, WEB-916,
   * WEB-967, WEB-1894): пользователей у них не осталось, а каждый байт ядра едет
   * покупателю в первом экране. `serviceUnavailable` читателей больше нет
   * (WEB-4118: предпросмотр — заголовком `systemPage.service`). Пустой квиз или пейволл говорит `funnel`.
   */
  systemPage: PublicSystemPageStrings;
  crashTitle: string;
  crashSubtitle: string;
  reloadPage: string;
  checkoutContactSupport: string;
  /**
   * WEB-785 (возврат QA 2026-08-01): остаток английских служебных строк на
   * публичных страницах русских генераций — заглушки пустого блока «до/после»,
   * единицы веса/роста, кнопки покупки и баннера срочности, подписи блока
   * согласия, подпись спиннера и заглушка завершения квиза.
   */
  beforeImagePlaceholder: string;
  afterImagePlaceholder: string;
  /** Подписи единиц пикеров веса/роста (ключ = единица из блока). */
  unitLabels: Readonly<Record<string, string>>;
  /** Фолбэк главной кнопки покупки (button_to_pay / BuyNowButton). */
  payLabel: string;
  /** Фолбэк кнопки баннера срочности (urgency_banner). */
  claimLabel: string;
  /** Фолбэк кнопки-ссылки (button_link). */
  learnMoreLabel: string;
  /**
   * WEB-2967: надписи блока «Редирект» (redirect_url) покупателю — отсчёт до
   * ухода с живым числом секунд и ссылка на случай, если уход не начался.
   * Раньше были английскими литералами в разметке на любом языке воронки.
   */
  redirectingIn: (seconds: number) => string;
  redirectFallbackLink: string;
  /**
   * WEB-1907 (добор 2): фолбэки БАЗОВОГО рендерера (`PublicBlockRenderer`).
   * Пустое поле блока — это старая воронка, блок из ИИ-генерации или стёртый
   * автором текст; до этой карточки в таких местах покупателю показывали
   * английский литерал независимо от языка воронки («Enter your email»,
   * «I agree», «Pay with card»).
   */
  emailInputPlaceholder: string;
  textInputPlaceholder: string;
  /** Подпись одиночной галочки согласия (блок checkbox) без своего текста. */
  agreeLabel: string;
  /**
   * WEB-2158: строка под галочками блока «Явное согласие на чувствительные
   * данные». Объясняет посетителю правду о поведении: без галочки его пускают
   * дальше, а ответ этого экрана просто не сохраняется (решение по макету
   * 18.09). Строка ПЛЕЕРА, а не авторский текст: автор её не редактирует,
   * потому что она описывает поведение продукта, а не обещание клиента.
   */
  sensitiveConsentNote: string;
  /** Слово «Цена», когда тариф выбран, а сама цена ещё не подгрузилась. */
  priceLabel: string;
  /**
   * Кнопка hosted-чекаута Stripe. Отдельно от `payLabel` («Купить» / «Buy
   * Now») намеренно: там призыв купить, здесь — СПОСОБ оплаты, и подменять
   * одно другим значило бы поменять текст денежной кнопки.
   */
  payWithCardLabel: string;
  /**
   * Ревизия пачки 13 (WEB-1414): единый текст ссылки «Восстановить покупки»
   * (restore_purchases) — дефолт нового блока в редакторе, фолбэк рендера и
   * плейсхолдер панели свойств раньше жили тремя разными строками.
   */
  restorePurchasesLabel: string;
  /** Фолбэк подписи спиннера. */
  loadingLabel: string;
  /** Блок согласия: вводная фраза и названия документов + соединитель. */
  termsAcceptPrefix: string;
  termsDocLabel: string;
  privacyDocLabel: string;
  termsSeparator: string;
  /** Заглушка завершения квиза без прицепленного пейволла. */
  completedTitle: string;
  completedText: string;
  /**
   * WEB-1820: системный баннер согласия на cookie (ConsentBanner) был
   * захардкожен по-русски и торчал русским на воронках любого языка.
   */
  consentMessage: string;
  consentAccept: string;
  consentDecline: string;
  consentAriaLabel: string;
  /**
   * WEB-2145 п.4-5. `consentPolicyLink` — подпись ССЫЛКИ на политику в
   * баннере. Отдельная от `privacyDocLabel` намеренно: та стоит внутри фразы
   * «Я принимаю …» и в русском (и других падежных языках) склоняется —
   * «Политику конфиденциальности». Ссылка баннера самостоятельна и обязана
   * быть в именительном.
   * `consentRecallLabel` — подпись значка «передумать» (он же title).
   */
  consentPolicyLink: string;
  consentRecallLabel: string;
  /**
   * WEB-2162 п.2: подпись ссылки на наш документ «Данные о здоровье» в подвале
   * публичной страницы. Ссылка нужна ВСЕМ посетителям, а не только тем, кому
   * показывали баннер согласия, поэтому строка отдельная от cookie-фраз.
   */
  healthDataLink: string;
  /**
   * Репорт владельца 01.09: авторский БЛОК cookie_consent (не системный
   * баннер) — словарные дефолт текста и подпись ссылки на политику; кнопки
   * блока переиспользуют consentAccept/consentDecline. Без этого фолбэки
   * рендера и дефолты нового блока были захардкожены по-английски.
   */
  cookieBlockMessage: string;
  cookiePolicyLearnMore: string;
  /**
   * WEB-1821: служебные строки подачи цены (derivePriceDisplay) — подписи
   * «в день»/«в месяц», «Списание: …», «Потом …», «N дней бесплатно»,
   * «за 2 недели». Только слова: арифметика и валюта остаются в
   * derivePriceDisplay и от языка не зависят.
   */
  priceUnitPerMonth: string;
  priceUnitPerDay: string;
  priceBilled: (formatted: string) => string;
  priceThen: (formatted: string) => string;
  priceTrialFree: (days: number) => string;
  /** Период словами; '' — неизвестный интервал (вызывающий откатывается). */
  pricePeriodPhrase: (interval: string, count: number) => string;
  /**
   * WEB-1826: период тарифа ПРИ СУММЕ — «Всего $9.99 в месяц» / «a month».
   *
   * Зачем отдельная строка, а не соседние две:
   *  · `pricePeriodPhrase` — это ДЛИТЕЛЬНОСТЬ («за 2 недели» / «for 2 weeks»),
   *    другая роль и другой предлог: «Всего $9.99 за месяц» — не то же, что
   *    «в месяц», а «Всего $14 за 2 недели» и «каждые 2 недели» расходятся
   *    смыслом (сколько заплатил против того, как часто списывают);
   *  · `priceUnitPerMonth`/`priceUnitPerDay` — фиксированные подписи ровно
   *    двух периодов подачи (derivePriceDisplay), года и недели у них нет.
   * Таблицы единиц (RU_PERIOD_FORMS / EN_PERIOD_UNITS) переиспользуются —
   * второго списка названий периодов в файле быть не должно.
   *
   * '' — периода нет вовсе (разовый платёж, неизвестный интервал): подставлять
   * покупателю нечего, и вызывающий убирает плейсхолдер вместе с пробелом.
   */
  pricePerPeriodPhrase: (interval: string, count: number) => string;
  /**
   * WEB-785 (возврат QA lane D1 06.08): подписи таймера срочности (блок
   * `countdown`) были захардкожены по-английски независимо от языка
   * воронки — на публичном пейволле русской генерации показывали
   * "Days / Hours / Min / Sec". Два набора — по числу форматов таймера
   * (`resolveCountdownFormat`, countdown.ts): 'default' (Title Case под
   * крупными цифрами) и 'digits-labeled' (строчные, стиль BetterMe).
   * `mono-header` подписей не показывает вовсе — для него набора нет.
   */
  blockDefaults: PublicQuizBlockDefaults;
  countdownLabelsDefault: string[];
  countdownLabelsDigitsLabeled: string[];
  /**
   * WEB-1905: портал подписчика (отмена, возобновление, оффер удержания,
   * вход по письму) — публичная страница, значит язык воронки.
   */
  portal: PublicPortalStrings;
  /**
   * WEB-2115: надписи ссылки предпросмотра. Есть только у языков из пакетов;
   * у ядра (en, ru) поля нет намеренно — см. {@link PreviewLinkStrings}.
   */
  previewLink?: PreviewLinkStrings;
}

/** WEB-1821: русские формы единиц периода (винительный падеж, «за …»). */
const RU_PERIOD_FORMS: Record<string, readonly [string, string, string]> = {
  DAY: ['день', 'дня', 'дней'],
  WEEK: ['неделю', 'недели', 'недель'],
  MONTH: ['месяц', 'месяца', 'месяцев'],
  YEAR: ['год', 'года', 'лет'],
};

const RU_DAY_FORMS = ['день', 'дня', 'дней'] as const;

function ruPlural(
  n: number,
  forms: readonly [string, string, string],
): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return forms[0];
  if (mod10 >= 2 && mod10 <= 4 && !(mod100 >= 12 && mod100 <= 14))
    return forms[1];
  return forms[2];
}

const EN_PERIOD_UNITS: Record<string, string> = {
  DAY: 'day',
  WEEK: 'week',
  MONTH: 'month',
  YEAR: 'year',
};

/**
 * WEB-1905: образец адреса в поле почты портала. Одна строка на все
 * языки — это пример адреса, а не фраза: переводить в нём нечего.
 */
const PORTAL_EMAIL_PLACEHOLDER = 'you@example.com';

/** WEB-3826: одна строка у «квиз не найден» и «предложение не найдено» — одной константой (вес первого экрана). */
const SYSTEM_STALE_LINK_EN = 'The link may be outdated. Ask whoever sent it for a new one.';

const EN: PublicQuizStrings = {
  stepOf: (current, total) => `Step ${current} of ${total}`,
  counterOf: 'of',
  loaderTitle: 'Analyzing your answers...',
  loaderSteps: [
    { label: 'Processing your answers', durationMs: 1400 },
    { label: 'Calculating your results', durationMs: 1600 },
    { label: 'Preparing your plan', durationMs: 1400 },
  ],
  reviewsBasedOn: (count) => `Based on ${count} reviews`,
  chartStartLabel: 'Today',
  chartEndLabel: 'Goal',
  beforeLabel: 'BEFORE',
  afterLabel: 'AFTER',
  backLabel: 'Back',
  continueLabel: 'Continue',
  finishLabel: 'Finish',
  scratchRevealLabel: 'Reveal',
  blurTeaserLockedLabel: 'hidden until purchase',
  forecastChartLabel: 'Forecast chart',
  promoTicketTimerLabel: 'Time left on this offer',
  nowGoalNowLabel: 'Now',
  nowGoalGoalLabel: 'Your goal',
  bmiScaleLabel: 'BMI scale',
  requiredFieldWarning: 'This field is required',
  invalidEmailWarning: 'Please check your email address',
  closeLabel: 'Close',
  loaderSkipLabel: 'Skip',
  unmuteVideoAria: 'Unmute video',
  playVideoAria: 'Play video',
  ratingAria: (value, points) => `Rating ${value} out of ${points}`,
  planNoPriceFallback: 'Price unavailable',
  returnToAppFallback: 'Return to the app',
  openingAppHint:
    'Opening the app… If it is not installed, the store will open.',
  imageLoadError: 'Image failed to load',
  serviceUnavailable: 'Service is temporarily unavailable.',
  retry: 'Retry',
  ratingOutOf: (value, scale) => `${value} out of ${scale}`,
  checkoutUnavailable:
    'Payments are temporarily unavailable. Please try again later.',
  // WEB-2115: читает и посторонний получатель ссылки предпросмотра, и автор
  // в канве редактора — фраза верна обоим (раньше звала «опубликовать пейволл»).
  checkoutPreviewOnly: 'This is a preview — real payment is disabled',
  checkoutNoPrice:
    'Payments are temporarily unavailable: no price is configured.',
  checkoutAcceptTerms: 'Please accept the terms to continue.',
  checkoutFillRequired: 'Please fill in the required fields to continue.',
  checkoutStartFailed: 'Could not start the checkout. Please try again.',
  checkoutReloadNeeded: 'Please refresh the page and try again.',
  checkoutPriceUnavailable:
    'This price is no longer available. Refresh the page or contact the seller.',
  systemPage: {
    page: ['Page not found', 'Check the address or open the link again.'],
    funnel: ['Page unavailable', 'It can’t be opened right now. Please check back later.'],
    quiz: ['Quiz not found', SYSTEM_STALE_LINK_EN],
    paywall: ['Offer not found', SYSTEM_STALE_LINK_EN],
    service: ['Service unavailable', 'Please try again in a couple of minutes.'],
    author: ['This link is for the author', 'Sign in to your account in this browser and open the link again.'],
    signIn: 'Sign in',
  },
  crashTitle: 'Something went wrong',
  crashSubtitle: 'Please reload the page. If it happens again, try a little later.',
  reloadPage: 'Reload the page',
  checkoutContactSupport:
    'Could not start the payment. Nothing was charged — please contact support.',
  beforeImagePlaceholder: 'Before image',
  afterImagePlaceholder: 'After image',
  // stylepack/unit-picker-look: «in» — дюймы у роста в футах двумя полями.
  unitLabels: { kg: 'KG', lb: 'LB', cm: 'CM', ft: 'FT', in: 'IN' },
  payLabel: 'Buy Now',
  claimLabel: 'Claim Now',
  learnMoreLabel: 'Learn more',
  redirectingIn: (seconds) => `Redirecting in ${seconds}s...`,
  redirectFallbackLink: 'Click here if not redirected',
  emailInputPlaceholder: 'Enter your email',
  textInputPlaceholder: 'Type here...',
  agreeLabel: 'I agree',
  sensitiveConsentNote:
    'You can continue without ticking this — the answer just will not be saved.',
  priceLabel: 'Price',
  payWithCardLabel: 'Pay with card',
  restorePurchasesLabel: 'Restore purchases',
  loadingLabel: 'Loading...',
  termsAcceptPrefix: 'I accept the ',
  termsDocLabel: 'Terms',
  privacyDocLabel: 'Privacy Policy',
  termsSeparator: 'and',
  completedTitle: 'Thank you!',
  completedText: 'You completed the quiz.',
  consentMessage:
    'We use cookies and similar technologies for analytics and advertising. ' +
    'With your consent we share data about your actions with advertising ' +
    'networks (such as Meta and TikTok) to measure and target ads. You can ' +
    'accept or decline.',
  consentAccept: 'Accept',
  consentDecline: 'Decline',
  consentAriaLabel: 'Cookie consent',
  consentPolicyLink: 'Privacy Policy',
  consentRecallLabel: 'Change your cookie decision',
  healthDataLink: 'Health data',
  cookieBlockMessage: 'We use cookies to improve your experience.',
  cookiePolicyLearnMore: 'Learn more',
  priceUnitPerMonth: 'per month',
  priceUnitPerDay: 'per day',
  priceBilled: (formatted) => `Billed: ${formatted}`,
  priceThen: (formatted) => `Then ${formatted}`,
  priceTrialFree: (days) => `${days} ${days === 1 ? 'day' : 'days'} free`,
  pricePeriodPhrase: (interval, count) => {
    const unit = EN_PERIOD_UNITS[interval];
    if (!unit) return '';
    return `for ${count} ${count === 1 ? unit : `${unit}s`}`;
  },
  pricePerPeriodPhrase: (interval, count) => {
    const unit = EN_PERIOD_UNITS[interval];
    if (!unit) return '';
    return count === 1 ? `a ${unit}` : `every ${count} ${unit}s`;
  },
  blockDefaults: {
    microFeedbackTitle: 'We hear you 🫂',
    microFeedbackDetail: 'That is why we will start with a small step.',
    ratingAudience: '12,000 reviews',
    selfRelevanceQuestion: 'How much is this like you?',
    likertDisagreeLabel: 'Not at all',
    likertAgreeLabel: 'Completely like me',
    scratchCoverLabel: 'Scratch to reveal',
    scratchRevealTitle: 'Well done!',
    scratchRevealNote: 'Continue to the next step',
    blurTeaserTitle: 'Your report is ready',
    blurTeaserFirstLabel: 'Compatibility',
    blurTeaserFirstTeaser: 'High',
    blurTeaserSecondLabel: 'A big change',
    blurTeaserSecondTeaser: 'Coming soon',
    blurTeaserNote: 'Unlocks after purchase',
    forecastTitle: 'Your forecast',
    promoTicketTitle: 'Your promo code',
    nowGoalTitle: 'What will change',
    metricLabel: (index) => `Metric ${index}`,
    bmiTitle: 'Your body mass index',
    bmiValueLabel: 'Your BMI',
    bmiZoneUnderweight: 'Underweight',
    bmiZoneNormal: 'Normal',
    bmiZoneOverweight: 'Overweight',
    bmiZoneObese: 'Well above normal',
    bmiNote: 'A guideline, not a medical diagnosis',
    statementText: 'I put things off until the last minute',
    statementDisagreeLabel: 'Not me',
    statementNeutralLabel: 'Sometimes',
    statementAgreeLabel: 'That is me',
    preferenceQuestion: 'Does this interest you?',
    preferenceNoLabel: 'Not for me',
    preferenceYesLabel: 'Interesting',
    planBadge: 'New plan',
    loaderProcessingTitle: 'Analyzing your data...',
    iconWithTextTitle: 'Icon with text',
    accordionAnswer: 'Answer here',
    cardTitle: 'Card Title',
    cardDescription: 'Card description',
    datePickerPlaceholder: 'Select date',
    numberInputPlaceholder: 'Enter number',
    ratingStarsLabel: 'Rate your experience',
    toggleLabel: 'Enable this option',
    sensitiveConsentCollect:
      'I consent to the processing of my answers about my health',
    sensitiveConsentShare:
      'I allow these answers to be shared with the partners of this service',
    nameFieldPlaceholder: 'Your name',
    testimonialName: 'John D.',
    testimonialText: 'Great product!',
    featureChipLabels: ['Feature one', 'Feature two', 'Feature three', 'Feature four'],
    guaranteeBadge: '30-Day Money-Back Guarantee',
    // WEB-2451 п.9, решение владельца 24.09 (В101): остаток нигде не
    // считается — умолчание не обещает число.
    stockBadge: 'Only a few left',
    urgencyBanner: 'Limited time offer!',
    calloutText: 'Important information',
    multiStepQuestionTitle: 'Step questions',
    photoUploadQuestion: 'Upload a photo',
    paymentTermsTemplate: 'Billed {{price}} monthly. Cancel anytime.',
    downsellTitle: 'Wait — here is a special offer',
    downsellCta: 'Get discount',
    selectPlaceholder: 'Select...',
  },
  countdownLabelsDefault: ['Days', 'Hours', 'Min', 'Sec'],
  countdownLabelsDigitsLabeled: ['days', 'hours', 'minutes', 'seconds'],
  portal: {
    invalidLink: 'This page is for managing your subscription. To sign in, use the link from your email — or request a new one below.',
    expiredCode: 'This sign-in link has already been used or expired — each emailed link works only once. Request a new one below.',
    expiredManageLink: 'This management link has expired. Request a new one below.',
    sessionExpired: 'Your sign-in session has expired. Request a new sign-in link below.',
    loading: 'Loading subscriptions…',
    loadError: 'Could not load subscriptions. Please try again later.',
    empty: 'We could not find any subscriptions managed on this page. If you subscribed through the App Store or Google Play, manage that subscription in the store where you bought it.',
    cancelError: 'Could not cancel the subscription. Please try again.',
    cancelConfirmTitle: 'Are you sure you want to cancel your subscription?',
    cancelConfirmKeep: 'Keep subscription',
    cancelConfirmProceed: 'Cancel anyway',
    resumeError: 'Could not resume the subscription. Please try again.',
    resumePeriodEnded: 'This subscription has already ended — the paid period is over. To come back, please start a new subscription.',
    resumeSuccess: 'Your subscription is active again and will renew as usual.',
    downsellError: 'Could not apply the discount. Please try again.',
    downsellNotEligible: 'This discount cannot be applied while your subscription is paused or set to end. Resume or restart your subscription to become eligible.',
    downsellUnavailable: 'This discount is not available for your subscription.',
    downsellSuccess: 'Discount will apply from the next billing period.',
    entryPrompt: 'Enter the email you used for your purchase and we will send you a sign-in link.',
    entrySubmit: 'Email me a sign-in link',
    entrySent: "If this email is registered, we've sent a sign-in link. Check your inbox.",
    entrySendError: 'Could not send the link. Please try again.',
    entryNumberPrompt: 'We will email a sign-in link to the address from your purchase.',
    entryNumberSubmit: (email) => `Email a sign-in link to ${email}`,
    entryNumberSent: (email) => `Sign-in link sent to ${email}.`,
    entryNumberOtherEmail: 'Use another email',
    listTitle: 'Your subscriptions',
    cancelLabel: 'Cancel subscription',
    resumeLabel: 'Resume subscription',
    offerTitle: 'Wait — here is a discount',
    offerCta: 'Accept discount',
    cancellationScheduled: 'Cancellation scheduled',
    subscriptionCanceled: 'Subscription canceled',
    entryEmailPlaceholder: PORTAL_EMAIL_PLACEHOLDER,
    newPrice: (formatted) => `New price: ${formatted}`,
    cancellationScheduledOn: (date) =>
      `Cancellation scheduled for ${date}`,
    renewsOn: (date) => `Renews on ${date}`,
    statusLabels: {
      active: 'Active',
      trialing: 'Trial',
      past_due: 'Past due',
      unpaid: 'Unpaid',
      canceled: 'Canceled',
      incomplete: 'Incomplete',
      incomplete_expired: 'Expired',
      paused: 'Paused',
    },
  },
};

/** WEB-3826: одна строка у «квиз не найден» и «предложение не найдено» — одной константой (вес первого экрана). */
const SYSTEM_STALE_LINK_RU = 'Возможно, ссылка устарела. Попросите новую у того, кто её прислал.';

const RU: PublicQuizStrings = {
  stepOf: (current, total) => `Шаг ${current} из ${total}`,
  counterOf: 'из',
  loaderTitle: 'Анализируем ваши ответы...',
  loaderSteps: [
    { label: 'Обрабатываем ваши ответы', durationMs: 1400 },
    { label: 'Считаем результат', durationMs: 1600 },
    { label: 'Готовим план', durationMs: 1400 },
  ],
  reviewsBasedOn: (count) => `На основе ${count} отзывов`,
  chartStartLabel: 'Сегодня',
  chartEndLabel: 'Цель',
  beforeLabel: 'ДО',
  afterLabel: 'ПОСЛЕ',
  backLabel: 'Назад',
  continueLabel: 'Далее',
  finishLabel: 'Готово',
  scratchRevealLabel: 'Открыть',
  blurTeaserLockedLabel: 'скрыто до оплаты',
  forecastChartLabel: 'График-прогноз',
  promoTicketTimerLabel: 'Сколько осталось до конца предложения',
  nowGoalNowLabel: 'Сейчас',
  nowGoalGoalLabel: 'Цель',
  bmiScaleLabel: 'Шкала индекса массы тела',
  requiredFieldWarning: 'Это поле обязательное',
  invalidEmailWarning: 'Проверьте адрес почты',
  closeLabel: 'Закрыть',
  loaderSkipLabel: 'Пропустить',
  unmuteVideoAria: 'Включить звук',
  playVideoAria: 'Воспроизвести видео',
  ratingAria: (value, points) => `Оценка ${value} из ${points}`,
  planNoPriceFallback: 'Цена не указана',
  returnToAppFallback: 'Вернуться в приложение',
  openingAppHint:
    'Открываем приложение… Если оно не установлено, откроется магазин.',
  imageLoadError: 'Не удалось загрузить изображение',
  serviceUnavailable: 'Сервис временно недоступен.',
  retry: 'Повторить',
  ratingOutOf: (value, scale) => `${value} из ${scale}`,
  checkoutUnavailable: 'Оплата временно недоступна. Попробуйте позже.',
  checkoutPreviewOnly: 'Это предпросмотр — настоящая оплата недоступна',
  checkoutNoPrice: 'Оплата временно недоступна: не настроена цена.',
  checkoutAcceptTerms:
    'Поставьте галочку согласия с условиями, чтобы продолжить.',
  checkoutFillRequired:
    'Заполните обязательные поля на экране, чтобы продолжить.',
  checkoutStartFailed: 'Не удалось начать оплату. Попробуйте ещё раз.',
  checkoutReloadNeeded: 'Обновите страницу и попробуйте снова.',
  checkoutPriceUnavailable:
    'Эта цена больше недоступна. Обновите страницу или свяжитесь с продавцом.',
  systemPage: {
    page: ['Страница не найдена', 'Проверьте адрес или откройте ссылку ещё раз.'],
    funnel: ['Страница недоступна', 'Сейчас её нельзя открыть. Загляните позже.'],
    quiz: ['Квиз не найден', SYSTEM_STALE_LINK_RU],
    paywall: ['Предложение не найдено', SYSTEM_STALE_LINK_RU],
    service: ['Сервис недоступен', 'Попробуйте через пару минут.'],
    author: ['Ссылка для автора', 'Войдите в кабинет в этом браузере и откройте ссылку снова.'],
    signIn: 'Войти',
  },
  crashTitle: 'Что-то пошло не так',
  crashSubtitle: 'Перезагрузите страницу. Если повторится — попробуйте чуть позже.',
  reloadPage: 'Обновить страницу',
  checkoutContactSupport:
    'Не удалось начать оплату. Деньги не списаны — обратитесь в поддержку.',
  beforeImagePlaceholder: 'Фото «до»',
  afterImagePlaceholder: 'Фото «после»',
  unitLabels: { kg: 'кг', lb: 'фунты', cm: 'см', ft: 'футы', in: 'дюймы' },
  payLabel: 'Купить',
  claimLabel: 'Получить',
  learnMoreLabel: 'Подробнее',
  redirectingIn: (seconds) => `Переход через ${seconds} с…`,
  redirectFallbackLink: 'Нажмите здесь, если переход не начался',
  emailInputPlaceholder: 'Введите вашу почту',
  textInputPlaceholder: 'Напишите здесь...',
  agreeLabel: 'Я согласен',
  sensitiveConsentNote:
    'Без галочки можно идти дальше — ответ просто не сохранится.',
  priceLabel: 'Цена',
  payWithCardLabel: 'Оплатить картой',
  restorePurchasesLabel: 'Восстановить покупки',
  loadingLabel: 'Загрузка…',
  termsAcceptPrefix: 'Я принимаю ',
  termsDocLabel: 'Условия',
  privacyDocLabel: 'Политику конфиденциальности',
  termsSeparator: 'и',
  completedTitle: 'Спасибо!',
  completedText: 'Вы прошли квиз.',
  consentMessage:
    'Мы используем файлы cookie и похожие технологии для аналитики и ' +
    'рекламы. С вашего согласия мы передаём данные о ваших действиях ' +
    'рекламным сетям (например, Meta и TikTok), чтобы измерять и настраивать ' +
    'рекламу. Вы можете принять или отклонить.',
  consentAccept: 'Принять',
  consentDecline: 'Отклонить',
  consentAriaLabel: 'Согласие на использование cookie',
  consentPolicyLink: 'Политика конфиденциальности',
  consentRecallLabel: 'Изменить решение о cookie',
  healthDataLink: 'Данные о здоровье',
  cookieBlockMessage: 'Мы используем cookie, чтобы улучшить ваш опыт.',
  cookiePolicyLearnMore: 'Подробнее',
  priceUnitPerMonth: 'в месяц',
  priceUnitPerDay: 'в день',
  priceBilled: (formatted) => `Списание: ${formatted}`,
  priceThen: (formatted) => `Потом ${formatted}`,
  priceTrialFree: (days) =>
    `${days} ${ruPlural(days, RU_DAY_FORMS)} бесплатно`,
  pricePeriodPhrase: (interval, count) => {
    const forms = RU_PERIOD_FORMS[interval];
    if (!forms) return '';
    if (count === 1) return `за ${forms[0]}`;
    return `за ${count} ${ruPlural(count, forms)}`;
  },
  pricePerPeriodPhrase: (interval, count) => {
    const forms = RU_PERIOD_FORMS[interval];
    if (!forms) return '';
    // Один цикл — винительный падеж («в неделю», «в год»): это forms[0].
    if (count === 1) return `в ${forms[0]}`;
    // Кратный — «каждые 2 недели», «каждые 3 месяца»: форма по числу.
    return `каждые ${count} ${ruPlural(count, forms)}`;
  },
  blockDefaults: {
    microFeedbackTitle: 'Мы понимаем 🫂',
    microFeedbackDetail: 'Именно поэтому начнём с небольшого шага.',
    ratingAudience: '12 000 отзывов',
    selfRelevanceQuestion: 'Насколько это про вас?',
    likertDisagreeLabel: 'Совсем нет',
    likertAgreeLabel: 'Полностью про меня',
    scratchCoverLabel: 'Сотрите, чтобы открыть',
    scratchRevealTitle: 'Отлично!',
    scratchRevealNote: 'Переходите к следующему шагу',
    blurTeaserTitle: 'Ваш разбор готов',
    blurTeaserFirstLabel: 'Совместимость',
    blurTeaserFirstTeaser: 'Высокая',
    blurTeaserSecondLabel: 'Большая перемена',
    blurTeaserSecondTeaser: 'Уже скоро',
    blurTeaserNote: 'Откроется после оплаты',
    forecastTitle: 'Ваш прогноз',
    promoTicketTitle: 'Ваш промокод',
    nowGoalTitle: 'Что изменится',
    metricLabel: (index) => `Показатель ${index}`,
    bmiTitle: 'Ваш индекс массы тела',
    bmiValueLabel: 'Ваш ИМТ',
    bmiZoneUnderweight: 'Ниже нормы',
    bmiZoneNormal: 'Норма',
    bmiZoneOverweight: 'Выше нормы',
    bmiZoneObese: 'Значительно выше',
    bmiNote: 'Ориентир, а не медицинское заключение',
    statementText: 'Я откладываю дела до последнего',
    statementDisagreeLabel: 'Не про меня',
    statementNeutralLabel: 'Иногда',
    statementAgreeLabel: 'Это я',
    preferenceQuestion: 'Это вам интересно?',
    preferenceNoLabel: 'Не моё',
    preferenceYesLabel: 'Интересно',
    planBadge: 'Новый план',
    loaderProcessingTitle: 'Анализируем ваши данные...',
    iconWithTextTitle: 'Иконка с подписью',
    accordionAnswer: 'Ответ здесь',
    cardTitle: 'Заголовок карточки',
    cardDescription: 'Описание карточки',
    datePickerPlaceholder: 'Выберите дату',
    numberInputPlaceholder: 'Введите число',
    ratingStarsLabel: 'Оцените впечатление',
    toggleLabel: 'Включить эту опцию',
    sensitiveConsentCollect:
      'Я даю согласие на обработку моих ответов о здоровье',
    sensitiveConsentShare:
      'Я разрешаю передавать эти ответы партнёрам сервиса',
    nameFieldPlaceholder: 'Ваше имя',
    testimonialName: 'Иван П.',
    testimonialText: 'Отличный продукт!',
    featureChipLabels: ['Первая возможность', 'Вторая возможность', 'Третья возможность', 'Четвёртая возможность'],
    guaranteeBadge: 'Возврат денег в течение 30 дней',
    stockBadge: 'Мест осталось немного',
    urgencyBanner: 'Предложение ограничено по времени!',
    calloutText: 'Важная информация',
    multiStepQuestionTitle: 'Вопросы по шагам',
    photoUploadQuestion: 'Загрузите фото',
    paymentTermsTemplate: 'Списание {{price}} в месяц. Отмена в любой момент.',
    downsellTitle: 'Подождите — у нас есть особое предложение',
    downsellCta: 'Получить скидку',
    selectPlaceholder: 'Выберите...',
  },
  countdownLabelsDefault: ['Дни', 'Часы', 'Мин', 'Сек'],
  countdownLabelsDigitsLabeled: ['дни', 'часы', 'минуты', 'секунды'],
  portal: {
    invalidLink: 'Эта страница — для управления вашей подпиской. Чтобы войти, откройте ссылку из письма или запросите новую ниже.',
    expiredCode: 'Эта ссылка для входа уже использована или устарела — каждая ссылка из письма работает один раз. Запросите новую ниже.',
    expiredManageLink: 'Срок действия ссылки управления истёк. Запросите новую ниже.',
    sessionExpired: 'Ваш сеанс входа истёк. Запросите новую ссылку для входа ниже.',
    loading: 'Загружаем подписки…',
    loadError: 'Не удалось загрузить подписки. Попробуйте позже.',
    empty: 'Мы не нашли подписок, которыми управляет эта страница. Если вы оформили подписку в App Store или Google Play, управляйте ей в том магазине, где покупали.',
    cancelError: 'Не удалось отменить подписку. Попробуйте ещё раз.',
    cancelConfirmTitle: 'Точно отменить подписку?',
    cancelConfirmKeep: 'Оставить подписку',
    cancelConfirmProceed: 'Всё равно отменить',
    resumeError: 'Не удалось возобновить подписку. Попробуйте ещё раз.',
    resumePeriodEnded: 'Эта подписка уже закончилась — оплаченный период истёк. Чтобы вернуться, оформите подписку заново.',
    resumeSuccess: 'Подписка снова активна и продлится как обычно.',
    downsellError: 'Не удалось применить скидку. Попробуйте ещё раз.',
    downsellNotEligible: 'Скидку нельзя применить, пока подписка на паузе или отменяется. Снимите паузу или оформите подписку заново.',
    downsellUnavailable: 'Для вашей подписки эта скидка недоступна.',
    downsellSuccess: 'Скидка начнёт действовать со следующего периода.',
    entryPrompt: 'Введите почту, на которую оформляли покупку, — пришлём ссылку для входа.',
    entrySubmit: 'Прислать ссылку для входа',
    entrySent: 'Если эта почта у нас есть, мы отправили ссылку для входа. Проверьте почту.',
    entrySendError: 'Не удалось отправить ссылку. Попробуйте ещё раз.',
    entryNumberPrompt: 'Пришлём ссылку для входа на почту покупки.',
    entryNumberSubmit: (email) => `Отправить ссылку для входа на ${email}`,
    entryNumberSent: (email) => `Ссылка для входа отправлена на ${email}.`,
    entryNumberOtherEmail: 'Другая почта',
    listTitle: 'Ваши подписки',
    cancelLabel: 'Отменить подписку',
    resumeLabel: 'Возобновить подписку',
    offerTitle: 'Постойте — у нас есть скидка',
    offerCta: 'Получить скидку',
    cancellationScheduled: 'Отмена запланирована',
    subscriptionCanceled: 'Подписка отменена',
    entryEmailPlaceholder: PORTAL_EMAIL_PLACEHOLDER,
    newPrice: (formatted) => `Новая цена: ${formatted}`,
    cancellationScheduledOn: (date) =>
      `Отмена запланирована на ${date}`,
    renewsOn: (date) => `Продление ${date}`,
    statusLabels: {
      active: 'Активна',
      trialing: 'Пробный период',
      past_due: 'Просрочен платёж',
      unpaid: 'Не оплачена',
      canceled: 'Отменена',
      incomplete: 'Не завершена',
      incomplete_expired: 'Истекла',
      paused: 'На паузе',
    },
  },
};

/* ───────────────────────────────────────────────────────────────────────────
 * WEB-1874, требование В4-11 (решение владельца 04.09 ~11:00).
 *
 * ЧТО БЫЛО НЕ ТАК. Набор языков этого словаря был парой {en, ru}, а перевод
 * воронки автору предлагается на 31 языке (backend/src/info/services/
 * info.service.ts). Всё, кроме двух, молча падало в английский: испанская
 * воронка с испанским переводом показывала покупателю «Solo $79 a month», а
 * подсказка панели редактора обещала автору слово периода «на языке воронки».
 *
 * ЧТО ТЕПЕРЬ. Словарь знает ВЕСЬ набор языков перевода; равенство множеств
 * «языки перевода ↔ языки словаря» держит сторож в обе стороны
 * (`__tests__/publicQuizStrings.translationLanguages.web1874.test.ts`), а
 * `hasPublicQuizLocale` даёт честный ответ «этого языка у нас нет» — фолбэк в
 * английский остался прежним, но перестал быть НЕОТЛИЧИМЫМ от попадания.
 *
 * ПОЧЕМУ ПАКЕТ, А НЕ 29 КОПИЙ `PublicQuizStrings`. Половина ключей —
 * подстановки (`Шаг 3 из 7`, `На основе 120 отзывов`, «каждые 2 недели»), и
 * копия функции на каждый язык означала бы 29 мест, где легко разойтись
 * порядком аргументов. Пакет описывает ТОЛЬКО слова и шаблоны; сборка
 * (`packToStrings`) одна на все языки, а число и род внутри шаблона считает
 * `Intl.PluralRules` языка, а не наши правила.
 *
 * EN и RU НЕ ТРОНУТЫ намеренно: их строки закреплены живыми тестами
 * (WEB-967, WEB-1822, WEB-1826) побайтно, и пересборка их из пакета была бы
 * рефакторингом с риском ради красоты.
 * ─────────────────────────────────────────────────────────────────────────── */

/** Формы слова по категориям CLDR. `other` обязательна — она же и фолбэк. */
interface PluralPhrases {
  readonly zero?: string;
  readonly one?: string;
  readonly two?: string;
  readonly few?: string;
  readonly many?: string;
  readonly other: string;
}

/** Единица периода: формы по числу + готовая фраза для одного цикла. */
interface PeriodUnit {
  readonly forms: PluralPhrases;
  /** count === 1: «в месяц», «a month», «al mes», «im Monat». */
  readonly perOne: string;
  /**
   * Свой шаблон кратного периода, если общего мало. Французскому нужен род
   * («tous les 2 mois», но «toutes les 2 semaines»), и без переопределения
   * язык получил бы грамматически неверную фразу.
   */
  readonly every?: string;
}

/** Периоды, которые умеет подавать цена (те же, что у Stripe-интервалов). */
type PeriodInterval = 'DAY' | 'WEEK' | 'MONTH' | 'YEAR';

/**
 * Пакет одного языка. Плейсхолдеры в фигурных скобках: `{current}`,
 * `{total}`, `{n}`, `{value}`, `{points}`, `{scale}`, `{amount}`, `{unit}`.
 */
/**
 * WEB-1905: пакет надписей портала — только слова и шаблоны.
 *
 * Ревизия пачки 16 (находка Ф7). Ключи ВЫВОДЯТСЯ из `PublicPortalStrings`, а
 * не набираются вторым списком руками — тем же приёмом, что у
 * `PublicBlockDefaultsPack` выше: новая надпись портала обязана появиться у
 * всех 31 языка, и молчать про неё разбор пакета не может (второй конец
 * правила, CLAUDE.md/WEB-1844; ручной список молчит про новый ключ по
 * построению — шрам WEB-1687).
 *
 * Отличий от интерфейса три, и все они осознанные:
 *  - `entryEmailPlaceholder` не переводится (образец адреса один на все
 *    языки, `PORTAL_EMAIL_PLACEHOLDER`) — сборка подставляет его сама;
 *  - три подписи с подстановкой здесь ШАБЛОНЫ (`{amount}`, `{date}`), а не
 *    функции: значение подставляет `packToStrings`;
 *  - статусы сужены до известных ключей Stripe (`PortalStatusKey`).
 */
type PortalPack = Readonly<
  Omit<
    PublicPortalStrings,
    | 'entryEmailPlaceholder'
    | 'entryNumberSubmit'
    | 'entryNumberSent'
    | 'newPrice'
    | 'cancellationScheduledOn'
    | 'renewsOn'
    | 'statusLabels'
  > & {
    /** WEB-3413: плейсхолдер `{email}` — маска почты покупки. */
    entryNumberSubmit: string;
    entryNumberSent: string;
    /** Плейсхолдер `{amount}` — уже собранная цена со знаком валюты. */
    newPrice: string;
    /** Плейсхолдер `{date}` — дата в формате языка воронки. */
    cancellationScheduledOn: string;
    renewsOn: string;
    statusLabels: Readonly<Record<PortalStatusKey, string>>;
  }
>;

/** Статусы подписки Stripe, у которых есть своё слово покупателю. */
type PortalStatusKey =
  | 'active'
  | 'trialing'
  | 'past_due'
  | 'unpaid'
  | 'canceled'
  | 'incomplete'
  | 'incomplete_expired'
  | 'paused';

/**
 * WEB-1903: пакетная форма умолчаний содержимого. Ключи выводятся из
 * `PublicQuizBlockDefaults`, а не набираются вторым списком руками: новый ключ
 * обязан появиться у всех 31 языка, и молчать про него разбор пакета не может.
 * Отличие одно — подпись полосы здесь шаблон с `{n}`, число подставляет сборка.
 */
type PublicBlockDefaultsPack = Readonly<
  Omit<PublicQuizBlockDefaults, 'metricLabel'> & { metricLabel: string }
>;

export interface PublicLocalePack {
  /** Тег для `Intl.PluralRules` — по нему считаются формы числа. */
  readonly intlTag: string;
  /**
   * WEB-2384 п.6 (решение владельца 23.09.2026, вариант В): предвычисленные
   * выражения узнавания названий юридических документов для ЭТОГО языка.
   *
   * Едут вместе с надписями намеренно: страницы уже ждут пакет до первого
   * кадра, и второе ожидание было бы вторым концом одного правила. Ядро
   * (английский и русский) своих выражений тут не имеет — они всегда на руках
   * в `legalDocNames.public.ts`. Поле необязательное: пакет старой сборки,
   * доехавший из кеша браузера, выражений не принесёт, и язык воронки
   * отработает по ядру — как до WEB-2384.
   */
  readonly legalDocPatterns?: LegalDocPatternSources;
  readonly stepOf: string;
  /** Слово разделителя счётчика; нет — из шаблона `stepOf` (counterWordOf). */
  readonly counterOf?: string;
  readonly loaderTitle: string;
  readonly loaderSteps: readonly [string, string, string];
  readonly reviewsBasedOn: PluralPhrases;
  readonly chartStartLabel: string;
  readonly chartEndLabel: string;
  readonly beforeLabel: string;
  readonly afterLabel: string;
  readonly backLabel: string;
  readonly continueLabel: string;
  readonly finishLabel: string;
  readonly scratchRevealLabel: string;
  readonly blurTeaserLockedLabel: string;
  readonly forecastChartLabel: string;
  readonly promoTicketTimerLabel: string;
  readonly nowGoalNowLabel: string;
  readonly nowGoalGoalLabel: string;
  readonly bmiScaleLabel: string;
  readonly requiredFieldWarning: string;
  readonly invalidEmailWarning: string;
  readonly closeLabel: string;
  readonly loaderSkipLabel: string;
  readonly unmuteVideoAria: string;
  readonly playVideoAria: string;
  readonly ratingAria: string;
  readonly planNoPriceFallback: string;
  readonly returnToAppFallback: string;
  readonly openingAppHint: string;
  readonly imageLoadError: string;
  readonly serviceUnavailable: string;
  readonly retry: string;
  readonly ratingOutOf: string;
  readonly checkoutUnavailable: string;
  readonly checkoutPreviewOnly: string;
  readonly checkoutNoPrice: string;
  readonly checkoutAcceptTerms: string;
  readonly checkoutFillRequired: string;
  readonly checkoutStartFailed: string;
  readonly checkoutReloadNeeded: string;
  readonly checkoutPriceUnavailable: string;
  readonly purchaseThanks: PurchaseThanksPackStrings;
  readonly systemPage: PublicSystemPageStrings;
  readonly crashTitle: string;
  readonly crashSubtitle: string;
  readonly reloadPage: string;
  readonly checkoutContactSupport: string;
  readonly beforeImagePlaceholder: string;
  readonly afterImagePlaceholder: string;
  readonly unitLabels: Readonly<Record<'kg' | 'lb' | 'cm' | 'ft', string>>;
  readonly payLabel: string;
  readonly claimLabel: string;
  readonly learnMoreLabel: string;
  /** WEB-2967: шаблон с `{n}` — число секунд до ухода. */
  readonly redirectingIn: string;
  readonly redirectFallbackLink: string;
  // WEB-1907 (добор 2): фолбэки базового рендерера, см. PublicQuizStrings.
  readonly emailInputPlaceholder: string;
  readonly textInputPlaceholder: string;
  readonly agreeLabel: string;
  readonly sensitiveConsentNote: string;
  readonly priceLabel: string;
  readonly payWithCardLabel: string;
  readonly restorePurchasesLabel: string;
  readonly loadingLabel: string;
  readonly termsAcceptPrefix: string;
  readonly termsDocLabel: string;
  readonly privacyDocLabel: string;
  readonly termsSeparator: string;
  readonly completedTitle: string;
  readonly completedText: string;
  readonly consentMessage: string;
  readonly consentAccept: string;
  readonly consentDecline: string;
  readonly consentAriaLabel: string;
  readonly consentPolicyLink: string;
  readonly consentRecallLabel: string;
  readonly healthDataLink: string;
  readonly cookieBlockMessage: string;
  readonly cookiePolicyLearnMore: string;
  readonly priceUnitPerMonth: string;
  readonly priceUnitPerDay: string;
  readonly priceBilled: string;
  readonly priceThen: string;
  readonly priceTrialFree: PluralPhrases;
  /** «за 2 недели» / «for 2 weeks»: длительность оплаченного куска. */
  readonly periodFor: string;
  /** «каждые 2 недели» / «every 2 weeks»: как часто списывают. */
  readonly periodEvery: string;
  readonly periodUnits: Readonly<Record<PeriodInterval, PeriodUnit>>;
  readonly blockDefaults: PublicBlockDefaultsPack;
  readonly countdownLabelsDefault: readonly [string, string, string, string];
  readonly countdownLabelsDigitsLabeled: readonly [
    string,
    string,
    string,
    string,
  ];
  /** WEB-1905: надписи портала подписчика. */
  readonly portal: PortalPack;
  /** WEB-2115: надписи страницы получателя ссылки предпросмотра. */
  readonly previewLink: Readonly<PreviewLinkStrings>;
}

/** Длительности шагов лоадера одинаковы на всех языках — это анимация. */
const LOADER_STEP_DURATIONS = [1400, 1600, 1400] as const;

const pluralRulesCache = new Map<string, Intl.PluralRules>();

/**
 * Правила числа языка. Если сборка Node/браузера не знает тег (урезанная ICU),
 * берём английские: лучше грамматически бедная форма, чем падение публичной
 * страницы покупателя.
 */
function pluralRulesFor(tag: string): Intl.PluralRules {
  const cached = pluralRulesCache.get(tag);
  if (cached) return cached;
  let rules: Intl.PluralRules;
  try {
    rules = new Intl.PluralRules(tag);
  } catch {
    rules = new Intl.PluralRules('en');
  }
  pluralRulesCache.set(tag, rules);
  return rules;
}

function selectPhrase(tag: string, n: number, phrases: PluralPhrases): string {
  const category = pluralRulesFor(tag).select(n);
  return phrases[category] ?? phrases.other;
}

function fillTemplate(
  template: string,
  values: Readonly<Record<string, string | number>>,
): string {
  return template.replace(/\{(\w+)\}/g, (whole, key: string) =>
    key in values ? String(values[key]) : whole,
  );
}

/**
 * Слово между номерами в шаблоне «Шаг {current} из {total}» языка пакета:
 * одно слово из букв или «/»; иначе (номера в другом порядке, знаки, несколько
 * слов — ko, zh, hu) — «/».
 */
function counterWordOf(template: string): string {
  return /\{current\} (\p{L}+|\/) \{total\}/u.exec(template)?.[1] ?? '/';
}

/** Пакет-наследник: язык отличается от базового десятком фраз, не всеми. */
export function derivePack(
  base: PublicLocalePack,
  overrides: Partial<PublicLocalePack>,
): PublicLocalePack {
  return { ...base, ...overrides };
}

/** Сборка словаря плеера из пакета языка. Одна на все языки. */
function packToStrings(pack: PublicLocalePack): PublicQuizStrings {
  const unitPhrase = (interval: string, count: number): string | null => {
    const unit = pack.periodUnits[interval as PeriodInterval];
    if (!unit) return null;
    return selectPhrase(pack.intlTag, count, unit.forms);
  };

  return {
    stepOf: (current, total) =>
      fillTemplate(pack.stepOf, { current, total }),
    counterOf: pack.counterOf ?? counterWordOf(pack.stepOf),
    loaderTitle: pack.loaderTitle,
    loaderSteps: pack.loaderSteps.map((label, i) => ({
      label,
      durationMs: LOADER_STEP_DURATIONS[i],
    })),
    reviewsBasedOn: (count) =>
      fillTemplate(selectPhrase(pack.intlTag, count, pack.reviewsBasedOn), {
        n: count,
      }),
    chartStartLabel: pack.chartStartLabel,
    chartEndLabel: pack.chartEndLabel,
    beforeLabel: pack.beforeLabel,
    afterLabel: pack.afterLabel,
    backLabel: pack.backLabel,
    continueLabel: pack.continueLabel,
    finishLabel: pack.finishLabel,
    scratchRevealLabel: pack.scratchRevealLabel,
    blurTeaserLockedLabel: pack.blurTeaserLockedLabel,
    forecastChartLabel: pack.forecastChartLabel,
    promoTicketTimerLabel: pack.promoTicketTimerLabel,
    nowGoalNowLabel: pack.nowGoalNowLabel,
    nowGoalGoalLabel: pack.nowGoalGoalLabel,
    bmiScaleLabel: pack.bmiScaleLabel,
    requiredFieldWarning: pack.requiredFieldWarning,
    invalidEmailWarning: pack.invalidEmailWarning,
    closeLabel: pack.closeLabel,
    loaderSkipLabel: pack.loaderSkipLabel,
    unmuteVideoAria: pack.unmuteVideoAria,
    playVideoAria: pack.playVideoAria,
    ratingAria: (value, points) =>
      fillTemplate(pack.ratingAria, { value, points }),
    planNoPriceFallback: pack.planNoPriceFallback,
    returnToAppFallback: pack.returnToAppFallback,
    openingAppHint: pack.openingAppHint,
    imageLoadError: pack.imageLoadError,
    serviceUnavailable: pack.serviceUnavailable,
    retry: pack.retry,
    ratingOutOf: (value, scale) =>
      fillTemplate(pack.ratingOutOf, { value, scale }),
    checkoutUnavailable: pack.checkoutUnavailable,
    checkoutPreviewOnly: pack.checkoutPreviewOnly,
    checkoutNoPrice: pack.checkoutNoPrice,
    checkoutAcceptTerms: pack.checkoutAcceptTerms,
    checkoutFillRequired: pack.checkoutFillRequired,
    checkoutStartFailed: pack.checkoutStartFailed,
    checkoutReloadNeeded: pack.checkoutReloadNeeded,
    checkoutPriceUnavailable: pack.checkoutPriceUnavailable,
    purchaseThanks: pack.purchaseThanks,
    systemPage: pack.systemPage,
    crashTitle: pack.crashTitle,
    crashSubtitle: pack.crashSubtitle,
    reloadPage: pack.reloadPage,
    checkoutContactSupport: pack.checkoutContactSupport,
    beforeImagePlaceholder: pack.beforeImagePlaceholder,
    afterImagePlaceholder: pack.afterImagePlaceholder,
    unitLabels: pack.unitLabels,
    payLabel: pack.payLabel,
    claimLabel: pack.claimLabel,
    learnMoreLabel: pack.learnMoreLabel,
    redirectingIn: (seconds) =>
      fillTemplate(pack.redirectingIn, { n: seconds }),
    redirectFallbackLink: pack.redirectFallbackLink,
    emailInputPlaceholder: pack.emailInputPlaceholder,
    textInputPlaceholder: pack.textInputPlaceholder,
    agreeLabel: pack.agreeLabel,
    sensitiveConsentNote: pack.sensitiveConsentNote,
    priceLabel: pack.priceLabel,
    payWithCardLabel: pack.payWithCardLabel,
    restorePurchasesLabel: pack.restorePurchasesLabel,
    loadingLabel: pack.loadingLabel,
    termsAcceptPrefix: pack.termsAcceptPrefix,
    termsDocLabel: pack.termsDocLabel,
    privacyDocLabel: pack.privacyDocLabel,
    termsSeparator: pack.termsSeparator,
    completedTitle: pack.completedTitle,
    completedText: pack.completedText,
    consentMessage: pack.consentMessage,
    consentAccept: pack.consentAccept,
    consentDecline: pack.consentDecline,
    consentAriaLabel: pack.consentAriaLabel,
    consentPolicyLink: pack.consentPolicyLink,
    consentRecallLabel: pack.consentRecallLabel,
    healthDataLink: pack.healthDataLink,
    cookieBlockMessage: pack.cookieBlockMessage,
    cookiePolicyLearnMore: pack.cookiePolicyLearnMore,
    priceUnitPerMonth: pack.priceUnitPerMonth,
    priceUnitPerDay: pack.priceUnitPerDay,
    priceBilled: (formatted) =>
      fillTemplate(pack.priceBilled, { amount: formatted }),
    priceThen: (formatted) =>
      fillTemplate(pack.priceThen, { amount: formatted }),
    priceTrialFree: (days) =>
      fillTemplate(selectPhrase(pack.intlTag, days, pack.priceTrialFree), {
        n: days,
      }),
    pricePeriodPhrase: (interval, count) => {
      const unit = unitPhrase(interval, count);
      if (unit === null) return '';
      return fillTemplate(pack.periodFor, { n: count, unit });
    },
    pricePerPeriodPhrase: (interval, count) => {
      const declared = pack.periodUnits[interval as PeriodInterval];
      if (!declared) return '';
      if (count === 1) return declared.perOne;
      const unit = selectPhrase(pack.intlTag, count, declared.forms);
      return fillTemplate(declared.every ?? pack.periodEvery, {
        n: count,
        unit,
      });
    },
    portal: {
      ...pack.portal,
      entryEmailPlaceholder: PORTAL_EMAIL_PLACEHOLDER,
      entryNumberSubmit: (email) =>
        fillTemplate(pack.portal.entryNumberSubmit, { email }),
      entryNumberSent: (email) =>
        fillTemplate(pack.portal.entryNumberSent, { email }),
      newPrice: (formatted) =>
        fillTemplate(pack.portal.newPrice, { amount: formatted }),
      cancellationScheduledOn: (date) =>
        fillTemplate(pack.portal.cancellationScheduledOn, { date }),
      renewsOn: (date) => fillTemplate(pack.portal.renewsOn, { date }),
    },
    previewLink: pack.previewLink,
    blockDefaults: {
      ...pack.blockDefaults,
      metricLabel: (index) =>
        fillTemplate(pack.blockDefaults.metricLabel, { n: index }),
    },
    countdownLabelsDefault: [...pack.countdownLabelsDefault],
    countdownLabelsDigitsLabeled: [...pack.countdownLabelsDigitsLabeled],
  };
}

// ── Испанский ───────────────────────────────────────────────────────────────

// ── Немецкий ────────────────────────────────────────────────────────────────

// ── Французский ─────────────────────────────────────────────────────────────

// ── Итальянский ─────────────────────────────────────────────────────────────

// ── Португальский (европейский) ─────────────────────────────────────────────


// ── Нидерландский ───────────────────────────────────────────────────────────

// ── Шведский ────────────────────────────────────────────────────────────────

// ── Датский ─────────────────────────────────────────────────────────────────

// ── Норвежский (букмол) ─────────────────────────────────────────────────────

// ── Финский ─────────────────────────────────────────────────────────────────

// ── Польский ────────────────────────────────────────────────────────────────

// ── Чешский ─────────────────────────────────────────────────────────────────

// ── Украинский ──────────────────────────────────────────────────────────────

// ── Турецкий ────────────────────────────────────────────────────────────────

// ── Румынский ───────────────────────────────────────────────────────────────

// ── Венгерский ──────────────────────────────────────────────────────────────

// ── Греческий ───────────────────────────────────────────────────────────────

// ── Индонезийский ───────────────────────────────────────────────────────────

// ── Вьетнамский ─────────────────────────────────────────────────────────────

// ── Тайский ─────────────────────────────────────────────────────────────────

// ── Хинди ───────────────────────────────────────────────────────────────────

// ── Японский ────────────────────────────────────────────────────────────────

// ── Корейский ───────────────────────────────────────────────────────────────

// ── Китайский (упрощённый) ──────────────────────────────────────────────────


// ── Арабский ────────────────────────────────────────────────────────────────

// ── Иврит ───────────────────────────────────────────────────────────────────


/**
 * Набор языков публичного словаря. Ключи — РОВНО коды справочника языков
 * перевода (`backend/src/info/services/info.service.ts`), включая
 * региональные (`pt-BR`, `zh-CN`, `zh-TW`): равенство множеств в обе стороны
 * держит сторож
 * `utils/__tests__/publicQuizStrings.translationLanguages.web1874.test.ts`.
 * Разъехаться молча список больше не может — это и было требование В4-11.
 */
/**
 * WEB-2057 — ЯЗЫКИ ВНЕ ЯДРА И ИХ ФАЙЛЫ-ПАКЕТЫ.
 *
 * Ключ — код языка воронки, значение — имя файла в `quizStringPacks/`. Их два
 * только потому, что `zh` и `zh-CN` — один и тот же упрощённый китайский:
 * второго файла у него нет и быть не должно.
 *
 * Пакет приезжает ЛЕНИВО (`loadPublicQuizStrings`): покупателю испанской
 * воронки не за что платить байтами тайского и иврита. Список остаётся
 * ЕДИНСТВЕННОЙ правдой о наборе языков — его же читает сторож соответствия
 * справочнику языков перевода (`publicQuizStrings.translationLanguages.web1874`)
 * и сторож «у каждого языка есть файл пакета» (`publicQuizStringsPacks.web2057`).
 */
const PACK_FILE_BY_LOCALE = {
  es: 'es',
  de: 'de',
  fr: 'fr',
  it: 'it',
  pt: 'pt',
  'pt-BR': 'pt-BR',
  zh: 'zh',
  'zh-CN': 'zh',
  'zh-TW': 'zh-TW',
  ja: 'ja',
  ko: 'ko',
  ar: 'ar',
  he: 'he',
  hi: 'hi',
  tr: 'tr',
  pl: 'pl',
  nl: 'nl',
  sv: 'sv',
  da: 'da',
  no: 'no',
  fi: 'fi',
  cs: 'cs',
  el: 'el',
  uk: 'uk',
  ro: 'ro',
  hu: 'hu',
  th: 'th',
  vi: 'vi',
  id: 'id',
} as const;

/**
 * ЯДРО — языки, которые едут покупателю всегда. `en` потому, что это фолбэк
 * любого незнакомого языка (без него первая же воронка на языке вне набора
 * осталась бы без надписей), `ru` — язык большинства наших воронок: для них
 * ленивый кусок был бы вторым запросом на том же критическом пути (честная
 * цена, урок ревизии WEB-2054), а не экономией.
 */
const CORE_STRINGS: Readonly<Record<'en' | 'ru', PublicQuizStrings>> = {
  en: EN,
  ru: RU,
};

export type SupportedPublicQuizLocale =
  | keyof typeof CORE_STRINGS
  | keyof typeof PACK_FILE_BY_LOCALE;

/** Коды словаря — для сторожа соответствия со справочником языков перевода. */
export const PUBLIC_QUIZ_LOCALES: readonly SupportedPublicQuizLocale[] = [
  ...(Object.keys(CORE_STRINGS) as SupportedPublicQuizLocale[]),
  ...(Object.keys(PACK_FILE_BY_LOCALE) as SupportedPublicQuizLocale[]),
];

/** Приехавшие пакеты: код языка → готовые надписи. */
const loadedStrings = new Map<SupportedPublicQuizLocale, PublicQuizStrings>();

/** Начатые заказы пакетов — чтобы два блока не заказали один файл дважды. */
const inFlightPacks = new Map<string, Promise<void>>();

/**
 * ШОВ ДЛЯ ТЕСТОВОЙ ОБВЯЗКИ: синхронный добытчик пакета (тот же приём, что у
 * кусков рендерера, WEB-2054). Сотни существующих тестов читают надписи воронки
 * синхронно (`getPublicQuizStrings('es').backLabel`), и ждать в каждом из них
 * нечего. ЛЕНИВЫЙ путь проверяется отдельно и БЕЗ этой подмены
 * (`publicQuizStringsLazy.web2057.test.ts`) — иначе подмена спрятала бы гонку.
 */
type PackSyncResolver = (file: string) => PublicLocalePack | null;
let packSyncResolver: PackSyncResolver | null = null;

export function setPublicQuizStringsSyncResolver(
  resolver: PackSyncResolver | null,
): void {
  packSyncResolver = resolver;
}

/**
 * Текущий добытчик — чтобы набор, которому нужен ЛЕНИВЫЙ путь, мог снять шов и
 * вернуть ровно тот же, а не свою копию (ревизия пачки 36: в
 * `publicQuizStringsLazy.web2057.test.ts` возврат был обещан комментарием, но
 * не сделан).
 */
export function getPublicQuizStringsSyncResolver(): PackSyncResolver | null {
  return packSyncResolver;
}

/** Только для тестов: забыть приехавшие пакеты. */
export function __resetPublicQuizStringsForTests(): void {
  loadedStrings.clear();
  inFlightPacks.clear();
}

/** Пакет кладут СТАТИЧЕСКИ — так делает кабинет (см. registerPublicQuizStringPacksEagerly). */
export function registerPublicQuizStringsPack(
  locale: SupportedPublicQuizLocale,
  pack: PublicLocalePack,
): void {
  registerLegalDocPatternsSource(locale, pack.legalDocPatterns);
  loadedStrings.set(locale, packToStrings(pack));
}

/** Нижний регистр → код, как он записан в DICTIONARIES (`pt-br` → `pt-BR`). */
const LOCALE_BY_NORMALIZED_TAG: ReadonlyMap<
  string,
  SupportedPublicQuizLocale
> = new Map(
  PUBLIC_QUIZ_LOCALES.map((code) => [code.toLowerCase(), code] as const),
);

/**
 * Поиск словаря по тегу: сперва полный тег (`pt-BR` — свой словарь), затем
 * первичный (`es-419` → `es`). Ничего не нашли — null, а НЕ «английский»:
 * отличать «язык есть» от «подставили английский» умеет только вызывающий,
 * который знает, зачем спрашивал (см. hasPublicQuizLocale).
 */
function lookupPublicQuizLocale(
  language?: string | null,
): SupportedPublicQuizLocale | null {
  const tag = (language ?? '').trim().toLowerCase().replace(/_/g, '-');
  if (tag === '') return null;
  const exact = LOCALE_BY_NORMALIZED_TAG.get(tag);
  if (exact) return exact;
  const primary = tag.split('-')[0];
  return LOCALE_BY_NORMALIZED_TAG.get(primary) ?? null;
}

/**
 * WEB-1874 (В4-11): честный ответ «знаем ли мы этот язык». Нужен кабинету:
 * подсказка автора про `{{period}}` обещает слово периода на языке воронки —
 * для языка вне набора она обязана оговориться, а не врать.
 */
export function hasPublicQuizLocale(language?: string | null): boolean {
  return lookupPublicQuizLocale(language) !== null;
}

/** Normalizes a BCP-47 tag ("ru-RU", "RU", "pt-BR", "es-419") to a dictionary key. */
export function resolvePublicQuizLocale(
  language?: string | null,
): SupportedPublicQuizLocale {
  return lookupPublicQuizLocale(language) ?? 'en';
}

/**
 * Пакет языка уже на руках? Языки ядра — всегда.
 *
 * Спрашивают те, кто ОБЯЗАН дождаться: страница воронки и страница пейволла
 * не рисуют содержимое, пока надписи не на руках, — иначе покупатель успел бы
 * увидеть английский и перескок на свой язык следующим кадром.
 */
export function hasPublicQuizStringsLoaded(language?: string | null): boolean {
  const locale = resolvePublicQuizLocale(language);
  if (locale in CORE_STRINGS || loadedStrings.has(locale)) return true;
  // Синхронный добытчик (тестовая обвязка) отдаёт пакет сейчас же — значит он
  // и правда на руках, и держать страницу на экране ожидания незачем.
  return tryResolveSync(locale) !== null;
}

/**
 * Заказать пакет языка. Языки ядра — сразу готово; неизвестный язык — это `en`,
 * тоже готово. Повторный заказ того же файла ждёт первый, а не шлёт второй
 * запрос; отказ (файл унесло выкатом) НЕ запоминается навсегда — следующий
 * заказ пробует снова, а надписи до тех пор английские, а не пустые.
 */
export function loadPublicQuizStrings(
  language?: string | null,
): Promise<void> {
  const locale = resolvePublicQuizLocale(language);
  if (locale in CORE_STRINGS || loadedStrings.has(locale)) {
    return Promise.resolve();
  }
  const file = PACK_FILE_BY_LOCALE[locale as keyof typeof PACK_FILE_BY_LOCALE];
  const started = inFlightPacks.get(file);
  if (started) return started;
  const request = import(`./quizStringPacks/${file}.ts`)
    .then((module: { pack: PublicLocalePack }) => {
      rememberPack(file, module.pack);
    })
    .catch((error: unknown) => {
      // Забыть отказ: иначе один неудачный запрос оставил бы покупателя на
      // английском до конца сессии (шрам ревизии WEB-2054 у кусков рендерера).
      inFlightPacks.delete(file);
      throw error;
    });
  inFlightPacks.set(file, request);
  return request;
}

/**
 * Достать пакет синхронно через шов тестовой обвязки. В бою добытчика нет, и
 * функция всегда возвращает null — ждать пакет обязаны страницы.
 */
function tryResolveSync(
  locale: SupportedPublicQuizLocale,
): PublicQuizStrings | null {
  if (!packSyncResolver) return null;
  const file = PACK_FILE_BY_LOCALE[locale as keyof typeof PACK_FILE_BY_LOCALE];
  if (!file) return null;
  const pack = packSyncResolver(file);
  if (!pack) return null;
  rememberPack(file, pack);
  return loadedStrings.get(locale) ?? null;
}

/** Пакет доехал: разложить его по ВСЕМ кодам, которые на него смотрят. */
function rememberPack(file: string, pack: PublicLocalePack): void {
  const strings = packToStrings(pack);
  for (const [locale, packFile] of Object.entries(PACK_FILE_BY_LOCALE)) {
    if (packFile === file) {
      loadedStrings.set(locale as SupportedPublicQuizLocale, strings);
      // WEB-2384 п.6: тем же движением кладём выражения узнавания названий
      // юридических документов — у них то же ожидание, что у надписей.
      registerLegalDocPatternsSource(locale, pack.legalDocPatterns);
    }
  }
}

/**
 * Надписи воронки. Контракт (WEB-2057): пакет на руках → он; нет — английские,
 * то есть прежнее поведение незнакомого языка, а не пустой экран.
 *
 * Дождаться пакета обязаны СТРАНИЦЫ (`loadPublicQuizStrings` до показа
 * содержимого) — здесь ждать нельзя: функция синхронна и зовётся из рендера
 * примерно в дюжине мест.
 */
export function getPublicQuizStrings(
  language?: string | null,
): PublicQuizStrings {
  const locale = resolvePublicQuizLocale(language);
  const core = CORE_STRINGS[locale as keyof typeof CORE_STRINGS];
  if (core) return core;
  const ready = loadedStrings.get(locale);
  if (ready) return ready;
  return tryResolveSync(locale) ?? CORE_STRINGS.en;
}

const publicDateFormatCache = new Map<string, Intl.DateTimeFormat>();

/**
 * WEB-1905: дата на языке ВОРОНКИ (портал подписчика печатает ею конец
 * оплаченного периода).
 *
 * Почему здесь, а не в `i18n/format.ts`: там локаль — язык КАБИНЕТА
 * (`UiLocale`, ровно два значения), а публичной странице нужен язык воронки,
 * то есть тот же набор, что у надписей выше. Тег берётся у
 * `resolvePublicQuizLocale`, поэтому язык вне набора печатает дату
 * по-английски — ровно так же, как и надписи, без расхождения «текст
 * английский, дата испанская».
 */
export function formatPublicQuizDate(
  value: Date,
  options: Intl.DateTimeFormatOptions,
  language?: string | null,
): string {
  const tag = resolvePublicQuizLocale(language);
  const key = `${tag}|${JSON.stringify(options)}`;
  let formatter = publicDateFormatCache.get(key);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat(tag, options);
    publicDateFormatCache.set(key, formatter);
  }
  return formatter.format(value);
}
